import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { useTeamSlug } from '@/context/TeamSlugContext'

/**
 * A custom hook to fetch the upvote status for a list of feature requests
 * for the currently authenticated user.
 * @param {string[]} featureIds - An array of feature request IDs to check.
 * @returns An object containing the upvote status map, loading state, etc.
 */
export const useUpvoteStatus = (featureIds: string[]) => {
	const { session } = useAuth()
	const { isPortal } = useTeamSlug()

	return useQuery<Record<string, boolean>, Error>({
		// The query key includes the user ID and portal context to ensure data is user-specific.
		queryKey: ['upvoteStatus', session?.user.id, featureIds, isPortal],
		queryFn: async () => {
			const startTime = Date.now()

			// If the user is not logged in or there are no feature IDs, return an empty map.
			if (!session?.user.id || featureIds.length === 0) {
				return {}
			}

			try {
				// Use different endpoints based on portal context
				const endpoint = isPortal
					? '/api/portal/features/upvote-status'
					: '/api/public/features/upvote-status'

				// Call the backend endpoint to get the upvote status.
				const upvotedItems = await apiClient<{ request_id: string }[]>(
					endpoint,
					{ method: 'POST', data: { featureIds }, needsAuth: true }
				)

				// Transform the array of upvoted items into a more efficient lookup map.
				const statusMap: Record<string, boolean> = {}
				upvotedItems.forEach((upvote) => {
					statusMap[upvote.request_id] = true
				})

				const duration = Date.now() - startTime

				return statusMap
			} catch (error: any) {
				const duration = Date.now() - startTime

				// If authentication failed, return empty map instead of throwing
				if (error.code === 'NO_AUTH_TOKEN' || error.status === 401) {
					return {}
				}

				// Re-throw other errors
				throw error
			}
		},
		// Only run the query if the user is logged in and there are IDs to check.
		enabled: !!session?.user.id && featureIds.length > 0,
		staleTime: 5 * 60 * 1000, // Cache the status for 5 minutes.
		retry: (failureCount: number, error: any) => {
			// Don't retry authentication failures
			if (error.code === 'NO_AUTH_TOKEN' || error.status === 401) {
				return false
			}
			// Retry other errors up to 3 times
			return failureCount < 3
		},
	})
}
